import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Users, UserPlus, Settings, Shield, User, Crown, Trash } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { useUserRole } from '@/hooks/useUserRole'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface UserRole {
  id: string
  user_id: string
  role: 'admin' | 'senior' | 'junior' | 'user'
  created_at: string
  updated_at: string
}

interface Profile {
  id: string
  full_name?: string
  personnummer?: string
  email?: string
  avatar_url?: string
}

interface TeamMember extends Profile {
  role: UserRole['role']
  can_create_cases: boolean
  isSystemAdmin?: boolean
  last_active?: string
}

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
  role?: 'admin' | 'senior' | 'junior' | 'user'
}

interface TeamPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const getRoleLabels = (t: any) => ({
  admin: t('team.roles.admin'),
  senior: t('team.roles.senior'),
  junior: t('team.roles.junior'), 
  user: t('team.roles.user')
})

const roleColors = {
  admin: 'bg-destructive/10 text-destructive border-destructive/20',
  senior: 'bg-primary/10 text-primary border-primary/20',
  junior: 'bg-warning/10 text-warning border-warning/20',
  user: 'bg-muted text-muted-foreground border-muted'
}

const roleIcons = {
  admin: Crown,
  senior: Shield,
  junior: User,
  user: User
}

export function TeamPage({ organization, profile }: TeamPageProps) {
  const { t } = useTranslation()
  const { role } = useUserRole()
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [settingUpData, setSettingUpData] = useState(false)
  const { toast } = useToast()
  const [editOpen, setEditOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [editFullName, setEditFullName] = useState('')
  const [editPersonnummer, setEditPersonnummer] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [deletingMember, setDeletingMember] = useState(false)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [newMemberFullName, setNewMemberFullName] = useState('')
  const [newMemberPersonnummer, setNewMemberPersonnummer] = useState('')
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberRole, setNewMemberRole] = useState<UserRole['role']>('user')
  const [addingMember, setAddingMember] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  
  const roleLabels = getRoleLabels(t)

  useEffect(() => {
    fetchTeamMembers()
  }, [organization])

  const setupTestData = async () => {
    try {
      setSettingUpData(true)
      
      // Anropa funktionen för att sätta upp testdata
      const { error } = await supabase.rpc('setup_test_data_for_user')
      
      if (error) throw error
      
      toast({
        title: t("team.toast.setupComplete"),
        description: t("team.toast.setupDesc")
      })
      
      // Uppdatera team-listan
      await fetchTeamMembers()
      
    } catch (error) {
      console.error('Error setting up test data:', error)
      toast({
        title: t("team.toast.error"),
        description: t("team.toast.setupError"),
        variant: "destructive"
      })
    } finally {
      setSettingUpData(false)
    }
  }

  const fetchTeamMembers = async () => {
    try {
      // Resolve a valid organization id (fallback to Test Company if missing/invalid)
      const TEST_ORG_ID = '3dbc0127-c7f1-415f-83a9-a0f1b889562e'
      const orgId = (organization?.id && /^[0-9a-fA-F-]{36}$/.test(organization.id)) ? organization.id : TEST_ORG_ID

      // First get user_roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('organization_id', orgId)

      if (rolesError) {
        console.error('Error fetching user roles:', rolesError)
        throw rolesError
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!userRoles || userRoles.length === 0) {
        setIsAdmin(false);
        setTeamMembers([]);
        setCurrentUserId(user?.id ?? null);
        return;
      }
      if (user) {
        setCurrentUserId(user.id);
        const isAdminForOrg = userRoles.some(r => r.user_id === user.id && r.role === 'admin');
        // System admins och organisation admins kan hantera teammedlemmar
        setIsAdmin(isAdminForOrg || role === 'system_admin');
      } else {
        setIsAdmin(false);
      }

      // Get profiles for these users
      const userIds = userRoles.map(role => role.user_id)
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', userIds)

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError)
        // Continue without profiles data
      }

      // Transform data to match TeamMember interface
      const members: TeamMember[] = userRoles.map(roleData => {
        const userProfile = profiles?.find(p => p.id === roleData.user_id)
        const isSystemAdmin = roleData.role === 'system_admin'
        
        // Map database roles to UI roles
        const uiRole: 'admin' | 'senior' | 'junior' | 'user' = 
          roleData.role === 'admin' ? 'admin' :
          roleData.role === 'moderator' ? 'senior' : 'user'

        const displayName = userProfile?.full_name || (isSystemAdmin ? 'Systemadministratör' : '')

        return {
          id: roleData.user_id,
          full_name: displayName,
          personnummer: userProfile?.personnummer || '',
          email: userProfile?.email || '',
          avatar_url: userProfile?.avatar_url || '',
          role: uiRole,
          can_create_cases: ['admin', 'moderator'].includes(roleData.role),
          isSystemAdmin,
          last_active: roleData.updated_at
        }
      })
      
      setTeamMembers(members)
    } catch (error) {
      console.error('Error fetching team members:', error)
      toast({
        title: t("team.toast.error"),
        description: t("team.toast.fetchError"),
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const updateMemberRole = async (userId: string, newRole: UserRole['role']) => {
    try {
      // Resolve a valid organization id (fallback to Test Company if missing/invalid)
      const TEST_ORG_ID = '3dbc0127-c7f1-415f-83a9-a0f1b889562e'
      const orgId = (organization?.id && /^[0-9a-fA-F-]{36}$/.test(organization.id)) ? organization.id : TEST_ORG_ID

      // Map UI role to database role
      const dbRole: 'admin' | 'moderator' | 'user' = 
        newRole === 'admin' ? 'admin' :
        newRole === 'senior' ? 'moderator' : 'user'

      // Update role in database
      const { error } = await supabase
        .from('user_roles')
        .update({ 
          role: dbRole,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('organization_id', orgId)

      if (error) throw error

      // Update local state  
      setTeamMembers(prev => 
        prev.map(member => 
          member.id === userId ? { 
            ...member, 
            role: newRole,
            can_create_cases: ['admin', 'senior'].includes(newRole)
          } : member
        )
      )
      
      toast({
        title: t("team.toast.roleUpdated"),
        description: t("team.toast.roleDesc")
      })
    } catch (error) {
      console.error('Error updating member role:', error)
      toast({
        title: t("team.toast.error"),
        description: t("team.toast.roleError"),
        variant: "destructive"
      })
    }
  }

  const toggleCreatePermission = async (userId: string, canCreate: boolean) => {
    try {
      // Note: This is now controlled by role (admin/senior can create, junior/user cannot)
      // This function is kept for UI consistency but doesn't change database
      const currentMember = teamMembers.find(m => m.id === userId)
      if (!currentMember) return

      // Only admins and seniors can create cases
      const newRole = canCreate ? 
        (currentMember.role === 'user' ? 'junior' : currentMember.role) :
        (currentMember.role === 'admin' ? 'senior' : 'user')

      await updateMemberRole(userId, newRole)
      
      toast({
        title: "Behörighet uppdaterad", 
        description: `Rollen har ändrats för att ${canCreate ? 'tillåta' : 'begränsa'} skapande av ärenden`
      })
    } catch (error) {
      console.error('Error updating permissions:', error)
      toast({
        title: "Fel",
        description: "Kunde inte uppdatera behörigheter",
        variant: "destructive"
      })
    }
  }

  const openEdit = (member: TeamMember) => {
    setEditingMember(member)
    setEditFullName(member.full_name || '')
    setEditPersonnummer(member.personnummer || '')
    setEditEmail(member.email || '')
    setEditOpen(true)
  }

  const saveProfileChanges = async () => {
    if (!editingMember) return
    
    // Get the original member to check if role changed
    const originalMember = teamMembers.find(m => m.id === editingMember.id)
    const roleChanged = originalMember && originalMember.role !== editingMember.role
    
    // Validate personnummer format
    if (editPersonnummer && !editPersonnummer.match(/^\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}$/)) {
      toast({
        title: 'Ogiltigt personnummer',
        description: 'Personnummer måste vara i formatet ÅÅÅÅMMDD-XXXX',
        variant: 'destructive'
      })
      return
    }

    // Validate email format
    if (editEmail && !editEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: 'Ogiltig email',
        description: 'Ange en giltig emailadress',
        variant: 'destructive'
      })
      return
    }

    try {
      setSavingEdit(true)
      
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: editFullName,
          personnummer: editPersonnummer || null,
          email: editEmail || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingMember.id)

      if (profileError) throw profileError

      // Update role if changed
      if (roleChanged) {
        // Resolve organization id
        const TEST_ORG_ID = '3dbc0127-c7f1-415f-83a9-a0f1b889562e'
        const orgId = (organization?.id && /^[0-9a-fA-F-]{36}$/.test(organization.id)) ? organization.id : TEST_ORG_ID

        // Map UI role to database role
        const dbRole: 'admin' | 'moderator' | 'user' = 
          editingMember.role === 'admin' ? 'admin' :
          editingMember.role === 'senior' ? 'moderator' : 'user'

        const { error: roleError } = await supabase
          .from('user_roles')
          .update({ role: dbRole })
          .eq('user_id', editingMember.id)
          .eq('organization_id', orgId)

        if (roleError) throw roleError
      }

      setTeamMembers(prev => prev.map(m => m.id === editingMember.id ? {
        ...m,
        full_name: editFullName,
        personnummer: editPersonnummer,
        email: editEmail,
        role: editingMember.role
      } : m))

      toast({
        title: 'Profil uppdaterad',
        description: 'Användarens information har uppdaterats.'
      })
      setEditOpen(false)
    } catch (e: any) {
      console.error('Error updating profile:', e)
      toast({
        title: 'Fel',
        description: e.message?.includes('profiles_personnummer_uniq') ? 
          'Personnumret används redan av en annan användare' :
          e.message?.includes('profiles_email_uniq') ?
          'Emailadressen används redan av en annan användare' :
          'Kunde inte uppdatera profilen',
        variant: 'destructive'
      })
    } finally {
      setSavingEdit(false)
    }
  }

  const openAddMember = () => {
    setNewMemberFullName('')
    setNewMemberPersonnummer('')
    setNewMemberEmail('')
    setNewMemberRole('user')
    setAddMemberOpen(true)
  }

  const addTeamMember = async () => {
    // Validate input
    if (!newMemberFullName.trim()) {
      toast({
        title: 'Valideringsfel',
        description: 'Namn är obligatoriskt',
        variant: 'destructive'
      })
      return
    }

    if (!newMemberPersonnummer.match(/^\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}$/)) {
      toast({
        title: 'Ogiltigt personnummer',
        description: 'Personnummer måste vara i formatet ÅÅÅÅMMDD-XXXX',
        variant: 'destructive'
      })
      return
    }

    if (!newMemberEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: 'Ogiltig email',
        description: 'Ange en giltig emailadress',
        variant: 'destructive'
      })
      return
    }

    try {
      setAddingMember(true)
      
      // Get organization ID
      const TEST_ORG_ID = '3dbc0127-c7f1-415f-83a9-a0f1b889562e'
      const orgId = (organization?.id && /^[0-9a-fA-F-]{36}$/.test(organization.id)) ? organization.id : TEST_ORG_ID

      // Map UI role to database role
      const dbRole: 'admin' | 'moderator' | 'user' = 
        newMemberRole === 'admin' ? 'admin' :
        newMemberRole === 'senior' ? 'moderator' : 'user'

      const { data, error } = await supabase.rpc('add_team_member', {
        p_organization_id: orgId,
        p_full_name: newMemberFullName,
        p_personnummer: newMemberPersonnummer,
        p_email: newMemberEmail,
        p_role: dbRole
      })

      if (error) throw error

      toast({
        title: t('team.addMember.success.title'),
        description: t('team.addMember.success.description', { name: newMemberFullName })
      })

      // Optimistic UI update
      if (data) {
        setTeamMembers(prev => [
          ...prev,
          {
            id: data as string,
            full_name: newMemberFullName,
            personnummer: newMemberPersonnummer,
            email: newMemberEmail,
            avatar_url: '',
            role: newMemberRole,
            can_create_cases: ['admin', 'senior'].includes(newMemberRole),
            last_active: new Date().toISOString()
          }
        ])
      }

      setAddMemberOpen(false)
      await fetchTeamMembers()

    } catch (error: any) {
      console.error('Error adding team member:', error)
      toast({
        title: t('common.error'),
        description: error.message?.includes('already a member') ?
          t('team.addMember.errors.alreadyMember') :
          error.message?.includes('Only organization admins') ?
          t('team.addMember.errors.adminOnly') :
          error.message || t('team.addMember.errors.generic'),
        variant: 'destructive'
      })
    } finally {
      setAddingMember(false)
    }
  }

  const deleteTeamMember = async () => {
    if (!editingMember) return
    
    try {
      setDeletingMember(true)
      
      // Resolve a valid organization id (fallback to Test Company if missing/invalid)
      const TEST_ORG_ID = '3dbc0127-c7f1-415f-83a9-a0f1b889562e'
      const orgId = (organization?.id && /^[0-9a-fA-F-]{36}$/.test(organization.id)) ? organization.id : TEST_ORG_ID
      
      // Skydda systemadministratörer och dig själv från borttagning
      if (editingMember.isSystemAdmin) {
        toast({ title: t('team.delete.errors.protectedTitle'), description: 'Systemadministratör kan inte tas bort.', variant: 'destructive' })
        return
      }
      if (currentUserId && editingMember.id === currentUserId) {
        toast({ title: t('team.delete.errors.protectedTitle'), description: 'Du kan inte ta bort dig själv från organisationen.', variant: 'destructive' })
        return
      }

      // Förhindra att sista admin tas bort
      if (editingMember.role === 'admin') {
        const { data: admins, error: adminsError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('organization_id', orgId)
          .eq('role', 'admin')
          .neq('user_id', editingMember.id)
        if (adminsError) throw adminsError
        if (!admins || admins.length === 0) {
          toast({ title: t('team.delete.errors.protectedTitle'), description: t('team.delete.errors.lastAdmin') || 'Kan inte ta bort sista administratören.', variant: 'destructive' })
          return
        }
      }

      // Remove user role from organization
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', editingMember.id)
        .eq('organization_id', orgId)
      
      if (error) throw error
      
      toast({
        title: t('team.delete.success.title'),
        description: t('team.delete.success.description', { name: editingMember.full_name || t('team.user.unknown') })
      })
      
      setEditOpen(false)
      await fetchTeamMembers()
      
    } catch (error: any) {
      console.error('Error deleting team member:', error)
      toast({
        title: t('common.error'),
        description: error.message || t('team.delete.errors.generic'),
        variant: 'destructive'
      })
    } finally {
      setDeletingMember(false)
    }
  }

  const formatLastActive = (dateString?: string) => {
    if (!dateString) return t('team.lastActive.never')
    
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (minutes < 60) return t('team.lastActive.minutesAgo', { minutes })
    if (hours < 24) return t('team.lastActive.hoursAgo', { hours })
    return t('team.lastActive.daysAgo', { days })
  }

  const getInitials = (name?: string) => {
    if (!name) return '??'
    return name.split(' ').map(n => n[0]).join('').toUpperCase()
  }

  const getRoleStats = () => {
    const stats = teamMembers.reduce((acc, member) => {
      acc[member.role] = (acc[member.role] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    return stats
  }

  const stats = getRoleStats()
  

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('team.title.page')}</h1>
          <p className="text-muted-foreground">{t('team.subtitle.page')}</p>
        </div>
        <div className="flex gap-2">
          {teamMembers.length === 0 && (
            <Button 
              onClick={setupTestData} 
              disabled={settingUpData}
              variant="outline"
              className="gap-2"
            >
              {settingUpData ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              ) : (
                <Settings className="h-4 w-4" />
              )}
              {t('team.setupTest')}
            </Button>
          )}
          {isAdmin && (
            <Button className="gap-2" onClick={openAddMember}>
              <UserPlus className="h-4 w-4" />
              {t('team.addMember')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('team.stats.total')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{teamMembers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('team.stats.admins')}</CardTitle>
            <Crown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats.admin || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('team.stats.seniors')}</CardTitle>
            <Shield className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{stats.senior || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('team.stats.juniors')}</CardTitle>
            <User className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{stats.junior || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.table.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('team.table.user')}</TableHead>
                <TableHead>{t('team.table.role')}</TableHead>
                <TableHead>{t('team.table.canCreate')}</TableHead>
                <TableHead>{t('team.table.lastActive')}</TableHead>
                {isAdmin && <TableHead className="text-right">{t('team.table.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.map((member) => {
                const RoleIcon = roleIcons[member.role]
                
                return (
                  <TableRow key={member.id}>
                    <TableCell onClick={() => openEdit(member)} className="cursor-pointer">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback>{getInitials(member.full_name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{(member.full_name && member.full_name.trim()) || t('team.user.unknown')}</div>
                          <div className="text-sm text-muted-foreground">
                            {member.personnummer || member.email || t('team.user.noContact')}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <RoleIcon className="h-4 w-4" />
                        {isAdmin ? (
                          <Select
                            value={member.role}
                            onValueChange={(value: UserRole['role']) => updateMemberRole(member.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">{t('team.role.options.admin')}</SelectItem>
                              <SelectItem value="senior">{t('team.role.options.senior')}</SelectItem>
                              <SelectItem value="junior">{t('team.role.options.junior')}</SelectItem>
                              <SelectItem value="user">{t('team.role.options.user')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={roleColors[member.role]}>
                            {roleLabels[member.role]}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Switch
                          checked={member.can_create_cases}
                          onCheckedChange={(checked) => toggleCreatePermission(member.id, checked)}
                        />
                      ) : (
                         <div className="flex items-center gap-2">
                           <div className={`w-2 h-2 rounded-full ${member.can_create_cases ? 'bg-success' : 'bg-muted'}`} />
                           {member.can_create_cases ? t('common.yes') : t('common.no')}
                         </div>
                      )}
                    </TableCell>
                    <TableCell>{formatLastActive(member.last_active)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                         <Button variant="ghost" size="sm" className="gap-2" onClick={() => openEdit(member)}>
                           <Settings className="h-4 w-4" />
                           {t('team.table.manage')}
                         </Button>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

       {/* Permissions Info */}
       <Card>
         <CardHeader>
           <CardTitle>{t('team.permissions.title')}</CardTitle>
         </CardHeader>
         <CardContent>
           <div className="space-y-4">
             <div className="grid gap-4 md:grid-cols-2">
               <div>
                 <h4 className="font-medium mb-2">{t('team.permissions.admins.title')}</h4>
                 <ul className="text-sm text-muted-foreground space-y-1">
                   <li>• {t('team.permissions.admins.createManageAll')}</li>
                   <li>• {t('team.permissions.admins.inviteUsers')}</li>
                   <li>• {t('team.permissions.admins.changeRoles')}</li>
                   <li>• {t('team.permissions.admins.manageAnalysis')}</li>
                   <li>• {t('team.permissions.admins.seeAllWork')}</li>
                 </ul>
               </div>
               <div>
                 <h4 className="font-medium mb-2">{t('team.permissions.seniors.title')}</h4>
                 <ul className="text-sm text-muted-foreground space-y-1">
                   <li>• {t('team.permissions.seniors.createOwn')}</li>
                   <li>• {t('team.permissions.seniors.reviewJuniors')}</li>
                   <li>• {t('team.permissions.seniors.advancedAnalysis')}</li>
                   <li>• {t('team.permissions.seniors.approveReports')}</li>
                 </ul>
               </div>
               <div>
                 <h4 className="font-medium mb-2">{t('team.permissions.juniors.title')}</h4>
                 <ul className="text-sm text-muted-foreground space-y-1">
                   <li>• {t('team.permissions.juniors.seeAssigned')}</li>
                   <li>• {t('team.permissions.juniors.basicAnalysis')}</li>
                   <li>• {t('team.permissions.juniors.createDrafts')}</li>
                   <li>• {t('team.permissions.juniors.needApproval')}</li>
                 </ul>
               </div>
               <div>
                 <h4 className="font-medium mb-2">{t('team.permissions.users.title')}</h4>
                 <ul className="text-sm text-muted-foreground space-y-1">
                   <li>• {t('team.permissions.users.viewOnly')}</li>
                   <li>• {t('team.permissions.users.limitedAccess')}</li>
                   <li>• {t('team.permissions.users.noCreation')}</li>
                 </ul>
               </div>
             </div>
           </div>
         </CardContent>
       </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('team.edit.dialog.title')}</DialogTitle>
            <DialogDescription>{t('team.edit.dialog.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name">{t('team.edit.fields.name')}</Label>
              <Input
                id="full_name"
                value={editFullName}
                onChange={(e) => setEditFullName(e.target.value)}
                placeholder={t('team.edit.placeholder.name')}
              />
            </div>
            <div>
              <Label htmlFor="personnummer">{t('team.edit.fields.personnummer')}</Label>
              <Input
                id="personnummer"
                value={editPersonnummer}
                onChange={(e) => setEditPersonnummer(e.target.value)}
                placeholder={t('team.edit.placeholder.personnummer')}
                pattern="\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}"
              />
            </div>
            <div>
              <Label htmlFor="email">{t('team.edit.fields.email')}</Label>
              <Input
                id="email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder={t('team.edit.placeholder.email')}
              />
            </div>
            {isAdmin && (
              <div>
                <Label htmlFor="role">{t('team.addMember.fields.role')}</Label>
                <Select
                  value={editingMember?.role}
                  onValueChange={(value: UserRole['role']) => {
                    if (editingMember) {
                      setEditingMember({ ...editingMember, role: value })
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t('team.role.options.admin')}</SelectItem>
                    <SelectItem value="senior">{t('team.role.options.senior')}</SelectItem>
                    <SelectItem value="junior">{t('team.role.options.junior')}</SelectItem>
                    <SelectItem value="user">{t('team.role.options.user')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={deleteTeamMember} 
              disabled={deletingMember || editingMember?.isSystemAdmin || (editingMember?.id === currentUserId)}
              className="gap-2"
            >
              <Trash className="h-4 w-4" />
              {deletingMember ? t('team.delete.dialog.deleting') : t('team.delete.dialog.delete')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={saveProfileChanges} disabled={savingEdit}>
                {savingEdit ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('team.addMember.dialog.title')}</DialogTitle>
            <DialogDescription>{t('team.addMember.dialog.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="memberName">{t('team.addMember.fields.name')}</Label>
              <Input
                id="memberName"
                value={newMemberFullName}
                onChange={(e) => setNewMemberFullName(e.target.value)}
                placeholder={t('team.addMember.placeholder.name')}
              />
            </div>
            <div>
              <Label htmlFor="memberPersonnummer">{t('team.addMember.fields.personnummer')}</Label>
              <Input
                id="memberPersonnummer"
                value={newMemberPersonnummer}
                onChange={(e) => setNewMemberPersonnummer(e.target.value)}
                placeholder={t('team.addMember.placeholder.personnummer')}
                pattern="\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}"
              />
            </div>
            <div>
              <Label htmlFor="memberEmail">{t('team.addMember.fields.email')}</Label>
              <Input
                id="memberEmail"
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder={t('team.addMember.placeholder.email')}
              />
            </div>
            <div>
              <Label htmlFor="memberRole">{t('team.addMember.fields.role')}</Label>
              <Select
                value={newMemberRole}
                onValueChange={(value: UserRole['role']) => setNewMemberRole(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('team.role.options.admin')}</SelectItem>
                  <SelectItem value="senior">{t('team.role.options.senior')}</SelectItem>
                  <SelectItem value="junior">{t('team.role.options.junior')}</SelectItem>
                  <SelectItem value="user">{t('team.role.options.user')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={addTeamMember} disabled={addingMember}>
              {addingMember ? t('team.addMember.dialog.adding') : t('team.addMember.dialog.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}